// src/belediye/redux/slices/belediyeFeatureFlagsSlice.js
//
// Amaç:
// - Backend feature flag’lerini tek yerden yönetmek
// - close_approval modülü kapalıysa UI hiç render olmasın + API çağrıları yapılmasın
//
// Strateji (backend değiştirmeden):
// - "probe" yöntemi: GET /api/close-approval/close-requests/notifications?limit=1
//   - 200 => enabled
//   - 404 => disabled (backend NotFound ile “yokmuş gibi” davranıyor)
//   - diğer hata => fail-safe: disabled
//
// Notlar:
// - Bu slice fail-safe tasarlanmıştır: herhangi bir hata durumunda close_approval=false
// - BelediyeApp.js’te user yüklendikten sonra dispatch(fetchFeatureFlags()) önerilir.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { probeCloseApprovalEnabled } from "../../services/closeApprovalApi";
import { probeHalkMasasiEnabled } from "../../services/halkMasasiApi";

/**
 * Uygulama çapında feature flag’leri getir.
 * Şimdilik close_approval + halk_masasi var; ileride genişler.
 */
export const fetchFeatureFlags = createAsyncThunk(
  "belediyeFeatureFlags/fetchFeatureFlags",
  async (_, { rejectWithValue }) => {
    try {
      //   bağımsız probe: biri patlasa diğeri etkilenmesin
      const results = {
        close_approval: false,
        halk_masasi: false,
      };

      try {
        const closeApprovalEnabled = await probeCloseApprovalEnabled();
        results.close_approval = Boolean(closeApprovalEnabled);
      } catch (err) {
        // fail-safe: false
      }

      try {
        const halkMasasiEnabled = await probeHalkMasasiEnabled();
        results.halk_masasi = Boolean(halkMasasiEnabled);
      } catch (err) {
        // fail-safe: false
      }

      return results;
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.message ||
        (status
          ? `Feature flags probe failed (HTTP ${status})`
          : "Feature flags probe failed");

      return rejectWithValue(message);
    }
  }
);

const initialState = {
  flags: {
    // Fail-safe default: false
    close_approval: false,
    halk_masasi: false,
  },
  loaded: false,
  loading: false,
  error: null,
  // debug/telemetry (opsiyonel, ama pratik)
  lastFetchedAt: null,
};

const belediyeFeatureFlagsSlice = createSlice({
  name: "belediyeFeatureFlags",
  initialState,
  reducers: {
    /**
     * Manuel override (genelde test/debug için)
     * Üretimde nadiren gerekir ama faydalı.
     */
    setFeatureFlag(state, action) {
      const { key, value } = action.payload || {};
      if (!key) return;
      state.flags = state.flags || {};
      state.flags[key] = Boolean(value);
    },

    /**
     * Reset (logout sonrası veya hard reset senaryolarında)
     */
    resetFeatureFlags(state) {
      state.flags = { ...initialState.flags };
      state.loaded = false;
      state.loading = false;
      state.error = null;
      state.lastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        const flags = action.payload || {};

        // Güvenli merge: bilmediğimiz/ek flag’ler de taşınabilir
        state.flags = {
          ...(state.flags || {}),
          ...flags,
        };

        // Fail-safe: undefined gelirse false’a çek
        if (typeof state.flags.close_approval !== "boolean") {
          state.flags.close_approval = false;
        }
        if (typeof state.flags.halk_masasi !== "boolean") {
          state.flags.halk_masasi = false;
        }

        state.loading = false;
        state.loaded = true;
        state.error = null;
        state.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        // Fail-safe: hata -> tüm flag’ler kapalı kabul edilir
        state.flags = {
          ...(state.flags || {}),
          close_approval: false,
          halk_masasi: false,
        };

        state.loading = false;
        state.loaded = true; // “probe denendi” anlamında loaded=true
        state.error =
          action.payload ||
          action.error?.message ||
          "Feature flags fetch failed";
        state.lastFetchedAt = new Date().toISOString();
      });
  },
});

export const { setFeatureFlag, resetFeatureFlags } =
  belediyeFeatureFlagsSlice.actions;

export default belediyeFeatureFlagsSlice.reducer;

// ---------------------------------------------------------------------
// Selectors (UI tarafında standardize kullanım için)
// ---------------------------------------------------------------------

export const selectFeatureFlagsState = (state) => state?.belediyeFeatureFlags;

export const selectFeatureFlags = (state) =>
  selectFeatureFlagsState(state)?.flags || {
    close_approval: false,
    halk_masasi: false,
  };

export const selectIsFeatureFlagsLoaded = (state) =>
  Boolean(selectFeatureFlagsState(state)?.loaded);

export const selectIsFeatureFlagsLoading = (state) =>
  Boolean(selectFeatureFlagsState(state)?.loading);

export const selectCloseApprovalEnabled = (state) =>
  Boolean(selectFeatureFlags(state)?.close_approval);

export const selectHalkMasasiEnabled = (state) =>
  Boolean(selectFeatureFlags(state)?.halk_masasi);

/**
 * Generic getter: selectFeatureFlag(state, "close_approval")
 */
export const selectFeatureFlag = (state, key) => {
  const flags = selectFeatureFlags(state);
  return Boolean(flags?.[key]);
};
